import type { CampaignPhase, CampaignPlacement } from '../types';

export interface CampaignPhaseInput {
  assignmentStatus: string;
  installationDate: string;
  expectedRemovalDate?: string | null;
}

function parseIsoDate(value: string | null | undefined): Date | null {
  if (!value || value === '—') return null;
  const parsed = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Derive campaign phase from assignment status and dates — not a stale stored field.
 * Reference date defaults to today (local).
 */
export function deriveCampaignPhase(
  input: CampaignPhaseInput,
  referenceDate: Date = new Date(),
): CampaignPhase {
  return resolveCampaignPhaseAtDate(input, referenceDate);
}

export function resolveCampaignPhaseAtDate(
  input: CampaignPhaseInput,
  asOf: Date,
): CampaignPhase {
  const asOfDay = startOfDay(asOf);
  const install = parseIsoDate(input.installationDate);
  const removal = parseIsoDate(input.expectedRemovalDate);
  const installDay = install ? startOfDay(install) : null;
  const removalDay = removal ? startOfDay(removal) : null;

  if (input.assignmentStatus === 'operator_complete') {
    if (installDay && installDay > asOfDay) return 'planned';
    return 'previous';
  }

  if (input.assignmentStatus === 'sent_to_operator') {
    if (installDay && installDay > asOfDay) return 'planned';
    if (removalDay && removalDay < asOfDay) return 'previous';
    return 'current';
  }

  if (['pending', 'admin_pending_review', 'admin_pending'].includes(input.assignmentStatus)) {
    if (installDay && installDay > asOfDay) return 'planned';
    if (installDay && installDay <= asOfDay) return 'planned';
    return 'planned';
  }

  if (installDay && installDay > asOfDay) return 'planned';
  if (installDay && installDay <= asOfDay) return 'previous';
  return 'planned';
}

export function campaignPhaseInputFromPlacement(
  campaign: CampaignPlacement,
): CampaignPhaseInput {
  return {
    assignmentStatus: campaign.assignmentStatus ?? statusFromLegacyPhase(campaign.phase),
    installationDate: campaign.installationDate,
    expectedRemovalDate: campaign.expectedRemovalDate,
  };
}

function statusFromLegacyPhase(phase: CampaignPhase): string {
  switch (phase) {
    case 'current':
      return 'sent_to_operator';
    case 'previous':
      return 'operator_complete';
    case 'planned':
      return 'pending';
    default: {
      const _exhaustive: never = phase;
      return _exhaustive;
    }
  }
}

export function withResolvedPhase(
  campaign: CampaignPlacement,
  asOf: Date = new Date(),
): CampaignPlacement {
  const phase = resolveCampaignPhaseAtDate(campaignPhaseInputFromPlacement(campaign), asOf);
  if (phase === campaign.phase) return campaign;
  return { ...campaign, phase };
}

export function phaseLabel(phase: CampaignPhase): string {
  switch (phase) {
    case 'current':
      return 'Active';
    case 'previous':
      return 'Previous';
    case 'planned':
      return 'Planned';
    default: {
      const _exhaustive: never = phase;
      return _exhaustive;
    }
  }
}
